import React, { useState, useEffect, useMemo } from "react";

import { Grid, MenuItem } from "@mui/material";
import { Loop } from "@mui/icons-material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { FiLogOut, FiRotateCcw } from "react-icons/fi";
import { MdExpandMore } from "react-icons/md";

import { AttributeStatus } from "../../../../../../../../models/status";
import { useAppState } from "../../../../../../../../context/StateContext";
import Button, { Color } from "../../../../../../../../components/Form/Button";
import { StyledMenu } from "../../../../../../../../components/Generic/AnchorMenu";
import useStyles from "./styles";

type User = {
  id?: number;
  email: string;
  status?: string;
};

interface Props {
  user?: User;
  loading?: boolean;
  loadingActions?: boolean;
  isOnViewMode?: boolean;
  setIsViewMode: () => void;
  onSubmitForm: () => void;
  toggleUserStatus: (status: string) => void;
  goBackToPeopleTab: () => void;
  onSendResetPassword: (email: string) => void;
}

const FooterActions = ({
  user,
  loading,
  isOnViewMode,
  setIsViewMode,
  onSubmitForm,
  toggleUserStatus,
  goBackToPeopleTab,
  onSendResetPassword,
  loadingActions,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [isOnEditMode, setIsOnEditMode] = useState<boolean>(false);

  const hasUserHaveStatusActive = useMemo(() => user?.status === AttributeStatus.ACTIVE, [user]);

  const hasPermissionToEdit = useMemo(() => {
    const resources = currentUser?.resources.find(
      (resource) =>
        resource.name.toLowerCase() === "people" || resource.name.toLowerCase() === "users"
    );

    if (resources) {
      if (resources.roles_resources) return resources.roles_resources.edit;
    }

    return false;
  }, [currentUser]);

  useEffect(() => {
    if (user) setIsOnEditMode(true);
    else setIsOnEditMode(false);
  }, [user]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <footer className={classes.footerWrapper}>
      <Grid
        container
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        className={classes.footerSection}
      >
        {!!user && (
          <Grid item md={6} lg={6}>
            <StyledMenu
              className={classes.anchorContainer}
              id="extra-actions-menu"
              anchorEl={anchorEl}
              keepMounted={false}
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
              disableAutoFocusItem
              PaperProps={{
                style: {
                  transform: "translateY(-190px)",
                },
              }}
            >
              <MenuItem
                data-cy="user-action-change-status"
                onClick={() => {
                  setAnchorEl(null);
                  toggleUserStatus(
                    hasUserHaveStatusActive ? AttributeStatus.INACTIVE : AttributeStatus.ACTIVE
                  );
                }}
                className={classes.anchorItem}
              >
                <Loop className={classes.footerIconButton} />
                {hasUserHaveStatusActive ? translate("inactiveUser") : translate("activeUser")}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                }}
                className={classes.anchorItem}
                data-cy="user-action-logout"
              >
                <FiLogOut className={classes.footerIconButton} />
                {translate("logUserOut")}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  onSendResetPassword(user?.email);
                  setAnchorEl(null);
                }}
                className={classes.anchorItem}
                data-cy="user-action-reset-pwd"
              >
                <FiRotateCcw className={classes.footerIconButton} />
                {translate("resetUserPassword")}
              </MenuItem>
            </StyledMenu>

            <Button
              data-cy="user-actions"
              customColor={Color.SECONDARY}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              loading={loadingActions}
              disabled={loading || loadingActions}
              className={classNames(
                classes.actionsButton,
                !!anchorEl && classes.anchorButtonActive
              )}
            >
              {translate("actions")}
              <MdExpandMore size={20} color="#FFF" className={classes.ml8} />
            </Button>
          </Grid>
        )}
        <Grid
          item
          container
          md={isOnViewMode || !user ? 12 : 6}
          lg={isOnViewMode || !user ? 12 : 6}
          justifyContent="flex-end"
        >
          <Grid item>
            <Button
              data-cy="buttonToCancelOperation"
              customColor={Color.CANCEL}
              onClick={goBackToPeopleTab}
            >
              {isOnViewMode ? translate("back") : translate("cancel")}
            </Button>
          </Grid>
          <Grid item>
            {isOnViewMode && hasPermissionToEdit ? (
              <Button
                data-cy="changeToEditMode"
                customColor={Color.PRIMARY}
                onClick={setIsViewMode}
              >
                {translate("edit")}
              </Button>
            ) : (
              <Button
                data-cy="submitButton"
                customColor={Color.SUCCESS}
                onClick={() => onSubmitForm()}
                disabled={loading}
              >
                {isOnEditMode ? translate("update") : translate("create")}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </footer>
  );
};

export default FooterActions;
